import { useState, useEffect } from 'react';

type Breakpoint = 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl';

interface BreakpointConfig {
  xs: number;
  sm: number;
  md: number;
  lg: number;
  xl: number;
  '2xl': number;
}

// Ant Design aligned breakpoints
const breakpoints: BreakpointConfig = {
  xs: 0,
  sm: 576,
  md: 768,
  lg: 992,
  xl: 1200, // Updated to match Ant Design's xl breakpoint
  '2xl': 1600,
};

function getCurrentBreakpoint(width: number): Breakpoint {
  if (width >= breakpoints['2xl']) return '2xl';
  if (width >= breakpoints.xl) return 'xl';
  if (width >= breakpoints.lg) return 'lg';
  if (width >= breakpoints.md) return 'md';
  if (width >= breakpoints.sm) return 'sm';
  return 'xs';
}

export function useBreakpoint() {
  const [breakpoint, setBreakpoint] = useState<Breakpoint>(() => {
    if (typeof window === 'undefined') return 'md'; // SSR fallback
    return getCurrentBreakpoint(window.innerWidth);
  });

  const [windowWidth, setWindowWidth] = useState(() => {
    if (typeof window === 'undefined') return 768; // SSR fallback
    return window.innerWidth;
  });

  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      setWindowWidth(width);
      setBreakpoint(getCurrentBreakpoint(width));
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Helper functions for common breakpoint checks
  const isMobile = breakpoint === 'xs' || breakpoint === 'sm';
  const isTablet = breakpoint === 'md';
  const isDesktop = breakpoint === 'lg' || breakpoint === 'xl' || breakpoint === '2xl';
  const isMdAndUp = breakpoint === 'md' || breakpoint === 'lg' || breakpoint === 'xl' || breakpoint === '2xl';
  const isSmAndUp =
    breakpoint === 'sm' || breakpoint === 'md' || breakpoint === 'lg' || breakpoint === 'xl' || breakpoint === '2xl';
  const isLgAndUp = breakpoint === 'lg' || breakpoint === 'xl' || breakpoint === '2xl';
  const isXlAndUp = breakpoint === 'xl' || breakpoint === '2xl';

  return {
    isSmAndUp,
    breakpoint,
    windowWidth,
    isMobile,
    isTablet,
    isDesktop,
    isMdAndUp,
    isLgAndUp,
    isXlAndUp,
    // Direct breakpoint checks
    isXs: breakpoint === 'xs',
    isSm: breakpoint === 'sm',
    isMd: breakpoint === 'md',
    isLg: breakpoint === 'lg',
    isXl: breakpoint === 'xl',
    is2Xl: breakpoint === '2xl',
  };
}
